import { Component, computed, ElementRef, inject, signal, viewChild } from '@angular/core';
import {
  API_BASE,
  ApiToken,
  ApiTokenService,
  CALLS_PER_HOUR,
  MAX_TOKENS,
  NewApiToken,
} from '../../core/api-token-service';
import { AuthService } from '../../core/auth-service';
import { I18nService } from '../../core/i18n-service';

/**
 * The tokens the API is used with, and the block of instructions to hand an
 * agent.
 *
 * A dialog and not a page of its own: a route would make `api` a name no cortito
 * could ever be called again, and somebody may already have one.
 */
@Component({
  selector: 'app-api-dialog',
  templateUrl: './api-dialog.html',
})
export class ApiDialog {
  private readonly tokens = inject(ApiTokenService);
  private readonly auth = inject(AuthService);
  private readonly i18n = inject(I18nService);
  private readonly dialog = viewChild.required<ElementRef<HTMLDialogElement>>('dialog');

  protected readonly t = computed(() => this.i18n.t().api);
  protected readonly base = API_BASE;
  protected readonly docs = API_BASE.replace('/api/v1', '/llms.txt');
  protected readonly callsPerHour = CALLS_PER_HOUR;
  protected readonly maxTokens = MAX_TOKENS;

  protected readonly held = signal<ApiToken[]>([]);
  protected readonly loading = signal(false);
  protected readonly working = signal(false);
  protected readonly failed = signal(false);
  /** The one just made, which is the only moment its token can be read. */
  protected readonly fresh = signal<NewApiToken | null>(null);
  protected readonly copied = signal('');
  /** The token whose revoke button has been pressed once. */
  protected readonly confirming = signal('');

  /** What an agent is handed: the real token while it is still on screen. */
  protected readonly prompt = computed(() =>
    this.t().agentPrompt(this.base, this.fresh()?.token ?? this.t().yourToken),
  );

  open(): void {
    this.fresh.set(null);
    this.copied.set('');
    this.confirming.set('');
    this.failed.set(false);
    this.dialog().nativeElement.showModal();
    void this.load();
  }

  /**
   * A date the way the reader's language writes one, short: the day and the month,
   * and the year only when it is not this one. It sits on a line with two other
   * things on a telephone.
   */
  protected when(at: number | undefined): string {
    if (!at) {
      return this.t().never;
    }
    const day = new Date(at);
    const thisYear = day.getFullYear() === new Date().getFullYear();

    return day.toLocaleDateString(this.i18n.lang(), {
      day: 'numeric',
      month: 'short',
      ...(thisYear ? {} : { year: 'numeric' }),
    });
  }

  protected async create(field: HTMLInputElement): Promise<void> {
    if (this.working()) {
      return;
    }
    this.working.set(true);
    this.failed.set(false);
    try {
      const made = await this.tokens.create(field.value.trim());
      field.value = '';
      this.fresh.set(made);
      this.copied.set('');
      await this.load();
    } catch {
      this.failed.set(true);
    } finally {
      this.working.set(false);
    }
  }

  /** Pressed once it asks; pressed again it revokes, and nothing undoes that. */
  protected async revoke(token: ApiToken): Promise<void> {
    if (this.confirming() !== token.id) {
      this.confirming.set(token.id);
      return;
    }

    const userId = this.auth.user()?.uid;
    this.confirming.set('');
    if (!userId) {
      return;
    }
    if (this.fresh()?.tokenId === token.id) {
      this.fresh.set(null);
    }
    this.held.update((held) => held.filter((one) => one.id !== token.id));
    await this.tokens.revoke(userId, token.id).catch(() => this.load());
  }

  protected async copy(what: 'token' | 'prompt'): Promise<void> {
    const text = what === 'token' ? (this.fresh()?.token ?? '') : this.prompt();
    try {
      await navigator.clipboard.writeText(text);
      this.copied.set(what);
    } catch {
      // A browser that will not hand the clipboard over leaves it on screen to
      // be selected, which is what the block is drawn for anyway.
    }
  }

  private async load(): Promise<void> {
    const userId = this.auth.user()?.uid;
    if (!userId) {
      return;
    }
    this.loading.set(true);
    try {
      this.held.set(await this.tokens.listFor(userId));
    } catch {
      this.held.set([]);
    } finally {
      this.loading.set(false);
    }
  }
}
